import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../store';
import DecimalField from '../shared/DecimalField';
import VatPriceField from '../shared/VatPriceField';
import TargetLabelField from './TargetLabelField';
import { calcPriceInclVat, formatPriceWithCurrency } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import { can } from '../../utils/access';
import { applyPriceEntry, estimateLineInclVat, lineDiscountAmount, lineFromPart, lineMargin } from './salesUtils';

/**
 * Add or edit a single parts-sale line (part-backed, job-backed or free text).
 * Emits a line-spec via onSubmit; the caller decides whether it lands in a
 * local cart or a POST. Pass `initialPart` to prefill from a registry part,
 * `initialLine` to edit an existing cart/sale line (`isNew` when that line is
 * being priced before it enters the cart, e.g. a variable-priced job). Price
 * entry uses VatPriceField. The incl/excl mode opens the way the work-order
 * dialogs do: margin scheme and VAT-entered prices are gross, everything else
 * follows the org's default_price_includes_vat setting, so the dialog agrees
 * with the cart card and the row caption around it. Opening never rewrites the
 * line; only typing or clicking the toggle does, so an untouched row saves
 * with exactly the price it had.
 */
function SaleLineDialog({
  open,
  onClose,
  initialPart,
  initialLine,
  onSubmit,
  submitting,
  allowMargin = false,
  isNew = false,
  customerId = null,
}) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const canViewMargins =
    can(currentOrganization, 'costs.view_purchase_prices') && can(currentOrganization, 'costs.view_margins');
  const [line, setLine] = useState(null);
  const [withVat, setWithVat] = useState(false);
  const [priceInput, setPriceInput] = useState('');

  useEffect(() => {
    if (!open) return;
    let next;
    if (initialLine) {
      next = { ...initialLine };
    } else if (initialPart) {
      next = lineFromPart(initialPart);
    } else {
      next = {
        key: 'new',
        part_id: null,
        job_id: null,
        unit: 'kpl',
        description: '',
        target_label: '',
        quantity: '1',
        unit_price: '0',
        unit_price_incl_vat: orgDefaultVat ? '0' : null,
        price_entered_with_vat: orgDefaultVat,
        vat_rate: 25.5,
        is_margin_vat_scheme: false,
        discount_percent: '0',
      };
    }
    const storedGross = !!next.price_entered_with_vat && next.unit_price_incl_vat != null;
    const mode = next.is_margin_vat_scheme || storedGross || orgDefaultVat;
    setLine(next);
    setWithVat(mode);
    let shown;
    if (next.is_margin_vat_scheme) {
      shown = String(next.unit_price ?? '0');
    } else if (storedGross) {
      shown = String(next.unit_price_incl_vat);
    } else if (mode) {
      // Net-stored price shown gross for the org default. Display only: the
      // line keeps its net price until the seller types or toggles.
      shown = calcPriceInclVat(next.unit_price ?? 0, parseFloat(next.vat_rate) || 25.5).toFixed(2);
    } else {
      shown = String(next.unit_price ?? '0');
    }
    setPriceInput(shown);
  }, [open, initialPart, initialLine, orgDefaultVat]);

  if (!line) return null;

  const set = (field, val) => setLine((prev) => ({ ...prev, [field]: val }));
  const setPrice = (val) => {
    setPriceInput(val);
    setLine((prev) => applyPriceEntry(prev, val, withVat));
  };
  const setPriceMode = (newWithVat, nextValue) => {
    setWithVat(newWithVat);
    setPriceInput(nextValue);
    setLine((prev) => applyPriceEntry(prev, nextValue, newWithVat));
  };

  // Part- or job-backed lines never switch VAT scheme; only free-text lines do.
  // Their description IS editable (it prints on the lähete and the invoice) and
  // is prefilled with the part/job name, so clearing it is the seller's choice.
  const catalogueBacked = !!(line.part_id || line.job_id);
  // Both recompute as the seller types, which is the whole point: he is deciding
  // whether to give the discount, not reading back one he already gave.
  // Gross, because the line total right next to it is gross in this dialog
  // whatever the org's entry default is. A net saving beside a gross total
  // reads as a rounding error, not as VAT.
  const discountAmount = lineDiscountAmount(line, true);
  const margin = lineMargin(line);
  // A margin-scheme free-text row has the purchase price as an INPUT right above,
  // so repeating it as a read-only figure would just be the same number twice.
  const purchasePriceIsEditable = allowMargin && !catalogueBacked && !!line.is_margin_vat_scheme;
  const valid = (catalogueBacked || (line.description || '').trim()) && parseFloat(line.quantity) > 0;
  const isEdit = !!initialLine && !isNew;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle>{isEdit ? t('parts_sales.editLine') : t('parts_sales.addLine')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('parts_sales.description')}
            value={line.description}
            onChange={(e) => set('description', e.target.value)}
            size="small"
            fullWidth
            autoFocus={!catalogueBacked}
            helperText={line.part_id ? t('parts_sales.descriptionPartHelp') : undefined}
          />
          <TargetLabelField
            value={line.target_label || ''}
            onChange={(v) => set('target_label', v)}
            customerId={customerId}
            label={t('parts_sales.target')}
            placeholder={t('parts_sales.targetPlaceholder')}
            inputTestId="sale-line-target"
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <DecimalField
              label={line.unit === 'h' ? t('parts_sales.hours') : t('parts_sales.qty')}
              value={line.quantity}
              onChange={(v) => set('quantity', v)}
              size="small"
              sx={{ width: 90, flexShrink: 0 }}
            />
            <VatPriceField
              value={priceInput}
              onChange={setPrice}
              withVat={withVat}
              onModeChange={setPriceMode}
              vatRate={parseFloat(line.vat_rate) || 25.5}
              lockedInclVat={!!line.is_margin_vat_scheme}
              allowNegative={!line.is_margin_vat_scheme}
              autoFocus={!!line.job_id}
              sx={{ flex: 1 }}
            />
          </Box>
          <DecimalField
            label={t('parts_sales.discount')}
            value={line.discount_percent}
            onChange={(v) => set('discount_percent', v)}
            size="small"
            max={100}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            sx={{ maxWidth: 160 }}
          />
          {allowMargin && !catalogueBacked && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!line.is_margin_vat_scheme}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      // Re-apply the typed price under the new mode — margin
                      // stores it as the gross price, normal mode as excl/incl
                      // per the entry toggle. Flipping only the flag would
                      // charge a different amount than the field displays.
                      setLine((prev) => {
                        const next = { ...prev, is_margin_vat_scheme: checked };
                        return priceInput === '' ? next : applyPriceEntry(next, priceInput, withVat);
                      });
                    }}
                  />
                }
                label={<Typography variant="body2">{t('parts_sales.marginVatScheme')}</Typography>}
              />
              {line.is_margin_vat_scheme && (
                <DecimalField
                  label={t('parts_sales.purchasePrice')}
                  value={line.margin_vat_purchase_price || ''}
                  onChange={(v) => set('margin_vat_purchase_price', v)}
                  size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                  sx={{ width: 140 }}
                />
              )}
            </Box>
          )}
          {isNew && line.job_id && (
            <Typography variant="caption" color="text.secondary">
              {t('parts_sales.jobPriceNeeded')}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {t('parts_sales.lineTotal')}: {formatPriceWithCurrency(estimateLineInclVat(line))}
            {!line.is_margin_vat_scheme && ` (${t('incl. VAT')})`}
            {discountAmount != null && (
              <Typography component="span" variant="body2" color="text.secondary">
                {' · '}
                {t('parts_sales.discountGiven')} −{formatPriceWithCurrency(discountAmount)}
              </Typography>
            )}
          </Typography>
          {canViewMargins && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: '8px',
                bgcolor: 'action.hover',
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
              }}
              data-testid="sale-line-margin"
            >
              {!purchasePriceIsEditable && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('parts_sales.purchasePrice')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {margin.hasCost ? formatPriceWithCurrency(margin.unitCost) : '—'}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('parts_sales.marginAfterDiscount')}
                </Typography>
                {margin.hasCost ? (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    color={margin.amount < 0 ? 'error.main' : 'text.primary'}
                  >
                    {formatPriceWithCurrency(margin.amount)}
                    {margin.percent != null && ` (${formatQuantity(margin.percent)} %)`}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('parts_sales.marginNoCost')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!valid || submitting}
          onClick={() =>
            onSubmit({
              part_id: line.part_id || null,
              job_id: line.job_id || null,
              quantity: line.quantity || '1',
              unit_price: line.unit_price,
              unit_price_incl_vat: line.unit_price_incl_vat,
              price_entered_with_vat: !!line.price_entered_with_vat && !line.is_margin_vat_scheme,
              discount_percent: line.discount_percent || '0',
              description: line.description || '',
              target_label: line.target_label || '',
              is_margin_vat_scheme: !!line.is_margin_vat_scheme && !catalogueBacked,
              margin_vat_purchase_price: line.margin_vat_purchase_price || null,
            })
          }
          data-testid="sale-line-submit"
        >
          {submitting ? <CircularProgress size={20} /> : isEdit ? t('Save') : t('parts_sales.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SaleLineDialog;
